import React, { ReactNode } from 'react';
import { MoreVerticalUniversal } from '@duxton-web/icons';
import { Button, Dropdown } from '@duxton-web/ui';
import styled from 'styled-components';

import {
  isBalancedShuffleTimeSliceStrategy,
  isLegacyTimeSliceStrategy,
} from '@/features/experiment/constants';

interface AssignmentStrategyOptionProps {
  icon: ReactNode;
  title: string;
  subtitle: string;
  selected?: boolean;
  onSelect?: () => void;
  disabled?: boolean;
  extraMenu?: { key: string; title: string; subtitle: string }[];
  onSelectExtraItem?: (key: string) => void;
  selectedStrategy?: string;
  timeSliceType?: string;
}

export const getTimeSliceStrategyTitle = (strategy: string): string => {
  if (isLegacyTimeSliceStrategy(strategy)) return 'Time Slice (Sequential)';

  if (isBalancedShuffleTimeSliceStrategy(strategy)) {
    return 'Time Slice (Balanced Shuffle)';
  }

  return '';
};

const AssignmentStrategyOption = ({
  icon,
  title,
  subtitle,
  selected,
  onSelect,
  disabled,
  extraMenu,
  onSelectExtraItem,
  selectedStrategy,
  timeSliceType,
}: AssignmentStrategyOptionProps) => {
  return (
    <Wrapper
      selected={selected}
      onClick={() => {
        if (disabled) return;
        if (selected) return;
        onSelect();
      }}
      disabled={disabled}
    >
      <LogoWrapper selected={selected} disabled={disabled}>
        {icon}
      </LogoWrapper>
      <InfoWrapper>
        <Title>{getTimeSliceStrategyTitle(timeSliceType) || title}</Title>
        <Subtitle>{subtitle}</Subtitle>
      </InfoWrapper>
      {extraMenu?.length && !disabled && (
        <ExtraWrapper>
          <Dropdown
            placement="bottomRight"
            trigger={['click']}
            menu={{
              items: extraMenu.map(({ key, title, subtitle }) => ({
                key,
                label: (
                  <InfoWrapper style={{ width: '100%' }}>
                    <Title>{title}</Title>
                    <Subtitle>{subtitle}</Subtitle>
                  </InfoWrapper>
                ),
                style: { width: '100%' },
              })),
              selectable: true,
              selectedKeys: [selectedStrategy],
              onClick: ({ key, domEvent }) => {
                domEvent.stopPropagation();
                onSelectExtraItem?.(key);
              },
            }}
          >
            <Button
              size="small"
              shape="circle"
              style={{ border: 'none' }}
              onClick={(e) => {
                e.stopPropagation();
              }}
              icon={<MoreVerticalUniversal />}
            />
          </Dropdown>
        </ExtraWrapper>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div<{ selected?: boolean; disabled?: boolean }>`
  align-items: flex-start;
  border-radius: 12px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  flex: 1;
  gap: 8px;
  padding: 8px;
  border: ${(props) =>
    props.selected ? '1px solid #00B14F' : '1px solid #E8E8E8'};
  background: ${(props) =>
    props.selected
      ? 'rgba(0, 177, 99, 0.1)'
      : props.disabled
        ? '#F5F5F5'
        : '#FFF'};
`;

const LogoWrapper = styled.div<{ selected?: boolean; disabled?: boolean }>`
  align-items: flex-start;
  align-self: stretch;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  padding: 8px;
  width: 32px;
  background: ${(props) =>
    props.selected ? '#00B14F' : props.disabled ? '#E9E9E9' : '#EEF9F9'};
  color: ${(props) => (props.selected ? 'white' : null)};
`;

const InfoWrapper = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0px 4px;
`;

const ExtraWrapper = styled.div`
  margin-left: auto;
  justify-self: center;
  align-self: center;
`;

const Title = styled.div`
  font-size: 14px;
`;
const Subtitle = styled.div`
  font-size: 12px;
  color: #707070;
`;

export default AssignmentStrategyOption;
